import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    YES,
    PENDING, IDLE,
    VIEW, EDIT, ADD,
    ASC,
    DATE_TIME_FORMAT,
} from "const"
import { MtTaskCreate, MtDriveFile } from "server/models"
import {
    useInput,
    StateType,
    forceRerenderContent,
    deepCopy,
    hasProjectAccess,
} from "client/utils"
import {
    PRM_TASK,

    StoreType,
    TaskObjectProps,
    TaskParticipantList,
    TaskObserverList,
    TaskValidation,

    LoadAllTasks,
    LoadTaskAttachments,
    SortProjects,
    SortTasks,
    SaveTask,

    closeModal,
    updateModalUrl,
    setTaskEdit,
    setTaskReloadEdit,
    setTaskParticipantList,
    setTaskObserverList,
    setTaskList,
    setTaskAttachmentsView,

    moduleIds,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    CheckButton,
    FireIcon,
    Feedback,
    CKEditorAttachmentsEdit,
    DatePicker,
    Delimiter,
    SelectDropdown,
    SelectDropdownSearch,
    SelectDropdownItem,
    UserDropdown,
    UserDropdownList,
    TagEditor,
    Button,
    SpinnerIcon,
    Empty,

    TableNestedIds,
} from "client/components"

import {
    HighPriorityWrapper,
    HighPriorityTitle,
} from "./styled"


let deadlineTime: string = null
let isMounted: boolean

const Form = ({
    task,
}: TaskObjectProps) => {
    const baseRoute = useSelector((state: StoreType) => state.task.baseRoute)
    const attachmentsEdit = useSelector((state: StoreType) => state.task.attachmentsEdit)

    const taskList = useSelector((state: StoreType) => state.task.taskProjectGroupedListEdit)
    const taskGroupedIds = useSelector((state: StoreType) => state.task.taskProjectGroupedIdsEdit)

    const lifecyclePhaseList = useSelector((state: StoreType) => state.task.lifecyclePhaseListEdit)
    const lifecyclePhaseIds = useSelector((state: StoreType) => state.task.lifecyclePhaseIdsEdit)

    const taskParticipantList = useSelector((state: StoreType) => state.taskParticipant.taskParticipantList)
    const taskObserverList = useSelector((state: StoreType) => state.taskObserver.taskObserverList)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const projectList = useSelector((state: StoreType) => state.project.projectList)
    const [projectIds] = useState(SortProjects(Object.values(projectList), "name", ASC, {}))
    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)
    const projectUserList = useSelector((state: StoreType) => state.projectUser.projectUserList)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const {
        subTaskModuleAccess,
        lifecycleModuleAccess,
    } = packageSettings

    const [projectId, setProjectId] = useState(task.projectId)

    const [phaseId, setPhaseId] = useState(task.phaseId)
    const [phaseSearch, setPhaseSearch] = useState("")
    const [phaseIdsFiltered, setPhaseIdsFiltered] = useState<number[]>([])

    const [taskId, setTaskId] = useState(task.taskId)
    const [taskSearch, setTaskSearch] = useState("")
    const [parentTaskIds, setParentTaskIds] = useState<number[]>([])

    const [name, setName] = useInput(task.name)
    const [description, setDescription] = useState(task.description)
    const [creator, setCreator] = useState(task.creator)
    const [responsible, setResponsible] = useState(task.responsible)
    const [deadline, setDeadline] = useState(task.deadline ? new Date(task.deadline) : null)
    const [highPriority, setHighPriority] = useState(task.highPriority)

    const [participants, setParticipants] = useState<readonly number[]>([])
    const [observers, setObservers] = useState<readonly number[]>([])
    const [selectedTags, setSelectedTags] = useState<number[]>([])
    const [attachments, setAttachments] = useState<readonly MtDriveFile[]>([])

    const [validation, setValidation] = useState<TaskValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)


    useEffect(() => {
        isMounted = true
        if (task.id) {
            if (taskParticipantList && taskParticipantList[task.id]) {
                setParticipants(taskParticipantList[task.id])
            }
            if (taskObserverList && taskObserverList[task.id]) {
                setObservers(taskObserverList[task.id])
            }
            if (task.tags) {
                const tagIds: number[] = []
                task.tags.forEach((tag) => {
                    tagIds.push(tag.tagId)
                })
                setSelectedTags(tagIds)
            }
        } else {
            setCreator(userData.userId)
            setProjectId(project.id) // null or number
        }

        setAttachments(attachmentsEdit)

        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        const tasksFiltered = SortTasks(Object.values(taskList), "name", ASC, {
            name: taskSearch,
            status: {
                pending: true,
                inProgress: true,
                pendingReview: true,
                completed: false,
                deferred: true,
            },
        })

        const groupedIds = (taskGroupedIds && taskGroupedIds[projectId || 0]) || null
        const allowedTaskIds: number[] = []

        const addTaskIds = (nestedIds: readonly TableNestedIds[]) => {
            nestedIds.forEach((nested) => {
                if (nested.id === task.id) return // separate nested tasks and the current one
                allowedTaskIds.push(Number(nested.id))
                addTaskIds(nested.nestedIds)
            })
        }
        if (groupedIds) addTaskIds(groupedIds.nestedIds)

        const taskIds: number[] = []
        tasksFiltered.forEach((row) => {
            if (allowedTaskIds.includes(row.id)) taskIds.push(row.id)
        })
        setParentTaskIds(taskIds)
    }, [projectId, taskList, taskGroupedIds, taskSearch])

    useEffect(() => {
        const phaseIds: readonly number[] = (lifecyclePhaseIds && lifecyclePhaseIds[projectId]) || []
        const filteredIds = phaseIds.filter((id) => {
            const phase = lifecyclePhaseList[id]
            return phase && (
                (phase.name.toUpperCase().indexOf(phaseSearch.toUpperCase()) >= 0)
                || (phase.lifecycle.name.toUpperCase().indexOf(phaseSearch.toUpperCase()) >= 0)
            )
        })
        setPhaseIdsFiltered(filteredIds)
    }, [projectId, lifecyclePhaseList, lifecyclePhaseIds, phaseSearch])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = task.id
            ? `${baseRoute}/${VIEW}/${task.id}`
            : baseRoute

        forceRerenderContent(path, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newTaskData: MtTaskCreate = {
            accountId: task.accountId || userData.accountId,
            taskId,
            projectId,
            phaseId,
            name,
            description,
            creator,
            responsible,
            deadline,
            highPriority,
            status: task.status,
            statusChanged: task.statusChanged || new Date(),
        }

        SaveTask(
            task.id,
            newTaskData,
            participants,
            observers,
            attachments,
            selectedTags,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newTaskId) {
                    const newUrl = `${baseRoute}/${EDIT}/${res.newTaskId}`
                    if (modals.length) {
                        dispatch(updateModalUrl(newUrl))
                    } else {
                        forceRerenderContent(newUrl, dispatch)
                    }
                }
            }
            if (res.validation) return

            const resultTaskId = res.newTaskId || task.id

            dispatch(setTaskEdit({
                id: resultTaskId,
                ...newTaskData,
                tags: res.tags,
            }))

            const taskParticipantListCopy: TaskParticipantList = taskParticipantList ? deepCopy(taskParticipantList) : {}
            taskParticipantListCopy[resultTaskId] = participants
            dispatch(setTaskParticipantList(taskParticipantListCopy))

            const taskObserverListCopy: TaskObserverList = taskObserverList ? deepCopy(taskObserverList) : {}
            taskObserverListCopy[resultTaskId] = observers
            dispatch(setTaskObserverList(taskObserverListCopy))


            // reload list
            LoadAllTasks(userData.accountId, project.id, userData.userId)
            .then((res1) => {
                dispatch(setTaskList(res1))

                LoadTaskAttachments(resultTaskId).then((res2) => {
                    dispatch(setTaskAttachmentsView({ [resultTaskId]: res2 }))

                    if (mode === 1) onClose()
                    if (mode === 2) {
                        dispatch(setTaskReloadEdit(false))
                        setTimeout(() => {
                            dispatch(setTaskReloadEdit(true))
                        }, 0)
                    }
                })
            })
        })
    }

    const selectedProject = projectList[projectId] || null
    const selectedPhase = lifecyclePhaseList[phaseId] || null
    const selectedTask = taskList[taskId] || null

    let lifecycleModuleEnabled = false
    if (selectedProject) {
        const projectModules = JSON.parse(selectedProject.modules)
        lifecycleModuleEnabled = !!projectModules[moduleIds.lifecycle]
    }

    const addToProjectAccess = hasProjectAccess(PRM_TASK, ADD, selectedProject ? selectedProject.id : null)

    const accountUsers = (accountUserList && accountUserList[userData.accountId]) || []
    const projectUsers = (project.id && projectUserList && projectUserList[project.id]) || []
    const userListIds = project.id ? projectUsers : accountUsers

    if (deadline) {
        const hours = deadline.getHours()
        const hoursStr = hours < 10 ? `0${hours}` : hours.toString()

        const minutes = deadline.getMinutes()
        const minutesStr = minutes < 10 ? `0${minutes}` : minutes.toString()

        deadlineTime = `${hoursStr}:${minutesStr}`
    } else {
        deadlineTime = "20:00"
    }

    return (
        <PageForm>
            <Row>
                <Col xs={8}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={4}>
                    <HighPriorityWrapper>
                        <CheckButton
                            title={(
                                <HighPriorityTitle active={highPriority}>
                                    {lang.highPriority}
                                    <FireIcon active={highPriority} />
                                </HighPriorityTitle>
                            )}
                            checked={highPriority}
                            onChange={() => setHighPriority(!highPriority)}
                        />
                    </HighPriorityWrapper>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <CKEditorAttachmentsEdit
                        description={description}
                        setDescription={setDescription}
                        attachments={attachments}
                        setAttachments={setAttachments}
                    />

                    <FormGroup>
                        <Label>{lang.project}</Label>
                        <SelectDropdown title={selectedProject ? `${selectedProject.name} (${selectedProject.publicId})` : ""}>
                            <SelectDropdownItem onClick={() => {
                                setProjectId(null)
                                setPhaseId(null)
                                if (projectId !== null) setTaskId(null)
                            }}
                            >
                                <Empty />
                            </SelectDropdownItem>
                            {
                                projectIds.map((id) => (
                                    <SelectDropdownItem
                                        key={id}
                                        onClick={() => {
                                            setProjectId(id)
                                            if (projectId !== id) {
                                                setTaskId(null)
                                                setPhaseId(null)
                                            }
                                        }}
                                    >
                                        {`${projectList[id].name} (${projectList[id].publicId})`}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.projectId}</Feedback>
                        <Feedback>{selectedProject && !addToProjectAccess && lang.noAccessToAddTask}</Feedback>
                    </FormGroup>

                    {
                        lifecycleModuleAccess === YES
                        && lifecycleModuleEnabled
                        && (
                            <FormGroup>
                                <Label>{lang.lifecyclePhase}</Label>
                                <SelectDropdown title={selectedPhase ? `${selectedPhase.lifecycle.name} > ${selectedPhase.name}` : ""}>
                                    <SelectDropdownSearch
                                        value={phaseSearch}
                                        onChange={(e) => setPhaseSearch(e.target.value)}
                                        onClearSearch={() => setPhaseSearch("")}
                                    />
                                    <SelectDropdownItem onClick={() => setPhaseId(null)}>
                                        <Empty />
                                    </SelectDropdownItem>
                                    {
                                        phaseIdsFiltered.map((id) => (
                                            <SelectDropdownItem key={id} onClick={() => setPhaseId(id)}>
                                                {`${lifecyclePhaseList[id].lifecycle.name} > ${lifecyclePhaseList[id].name}`}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                                <Feedback>{validation && validation.phaseId}</Feedback>
                            </FormGroup>
                        )
                    }

                    {
                        subTaskModuleAccess === YES && (
                            <FormGroup>
                                <Label>{lang.parentTask}</Label>
                                <SelectDropdown title={selectedTask ? selectedTask.name : ""}>
                                    <SelectDropdownSearch
                                        value={taskSearch}
                                        onChange={(e) => setTaskSearch(e.target.value)}
                                        onClearSearch={() => setTaskSearch("")}
                                    />
                                    <SelectDropdownItem onClick={() => setTaskId(null)}>
                                        <Empty />
                                    </SelectDropdownItem>
                                    {
                                        parentTaskIds.map((id) => (
                                            <SelectDropdownItem key={id} onClick={() => setTaskId(id)}>
                                                {taskList[id].name}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                                <Feedback>{validation && validation.taskId}</Feedback>
                            </FormGroup>
                        )
                    }

                    <FormGroup>
                        <Label>{lang.creator}</Label>
                        <UserDropdown
                            selectedId={creator}
                            listIds={userListIds}
                            title={lang.changeCreator}
                            showDelete={false}
                            onChange={(userId) => setCreator(userId)}
                        />
                    </FormGroup>
                    <Delimiter />

                    <FormGroup>
                        <Label>{lang.responsible}</Label>
                        <UserDropdown
                            selectedId={responsible}
                            listIds={userListIds}
                            title={lang.changeResponsible}
                            showDelete
                            onChange={(userId) => setResponsible(userId)}
                        />
                    </FormGroup>
                    <Delimiter />

                    <FormGroup>
                        <Label>{lang.participants}</Label>
                        <UserDropdownList
                            selectedIds={participants}
                            listIds={userListIds}
                            title={lang.addParticipant}
                            onDelete={setParticipants}
                            onAdd={setParticipants}
                        />
                    </FormGroup>
                    <Delimiter />

                    <FormGroup>
                        <Label>{lang.observers}</Label>
                        <UserDropdownList
                            selectedIds={observers}
                            listIds={userListIds}
                            title={lang.addObserver}
                            onDelete={setObservers}
                            onAdd={setObservers}
                        />
                    </FormGroup>
                    <Delimiter />

                    <FormGroup>
                        <Label>{lang.deadline}</Label>
                        <DatePicker
                            selected={deadline}
                            onChange={(date: Date) => {
                                if (date) {
                                    const timeArray = deadlineTime.split(":")
                                    date.setHours(Number(timeArray[0]))
                                    date.setMinutes(Number(timeArray[1]))
                                }
                                setDeadline(date)
                            }}
                            dateFormat={DATE_TIME_FORMAT}
                            showTimeInput
                            customTimeInput={<CustomTimeInput />}
                            isClearable
                        />
                    </FormGroup>
                    <Delimiter />

                    <FormGroup>
                        <Label>{lang.tags}</Label>
                        <TagEditor
                            selectedIds={selectedTags}
                            onChange={(tagIds) => setSelectedTags(tagIds)}
                        />
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button
                        variant="success"
                        row
                        disabled={selectedProject && !addToProjectAccess}
                        onClick={() => onSave(1)}
                    >
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button
                        variant="success"
                        row
                        disabled={selectedProject && !addToProjectAccess}
                        onClick={() => onSave(2)}
                    >
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form


interface CustomTimeInputProps {
    onChange?: (time: string) => void
}
const CustomTimeInput = ({
    onChange, // actually not used, but required by the syntax
}: CustomTimeInputProps) => (
    <input
        type="time"
        value={deadlineTime}
        onChange={(e) => {
            deadlineTime = e.target.value
            onChange(e.target.value)
        }}
    />
)
